import { FunctionComponent, useEffect, useState } from "react";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import LoadingWidget from "../../../context_providers/modal/loader";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import { fetchGet, fetchPost, fetchPut } from "../../../service/api_client";

import {
  WAREHOUSE_SUB_LEVEL_OPTIONS,
  subLevelToType,
  warehouseDefaultValue,
  WarehouseModel,
} from "../models/warehouse_model";
import { urls } from "../../../utils/urls";
import PartySearchField from "../../accounts/parties/party_search_field";
import { Country } from "country-state-city";
import RequiredMark from "../../../ui/new_components/common/required_mark";
import { stateData } from "../../../utils/states_data";
import {
  matchStateName,
  PincodeHint,
  usePincodeCheck,
} from "../../../ui/new_components/common/location/usePincodeCheck";
import { dataTypes } from "../../../utils/import_export_parser";
import { UserModel } from "../../../context_providers/auth/user_model";
import "../../../styles/responsive_shrink.css";

type CountryType = {
  isoCode: string;
  name: string;
};

interface InventoryBasketCreateScreenProps {
  edit: boolean;
  onClose: (id?: WarehouseModel) => void;
  data?: WarehouseModel;
}

const InventoryBasketCreateScreen: FunctionComponent<
  InventoryBasketCreateScreenProps
> = (props) => {
  const isUpdatePage = useRouteMatch(
    `${urls.procurement_masters_warehouse}/update/:id`,
  );
  const { id } = useParams<{ id: string }>();
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/warehouse_type`;
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const { edit } = props;
  const [editable, setEditable] = useState(false);
  const [data, setData] = useState<WarehouseModel>(
    props.data || ({} as WarehouseModel),
  );
  const navaigation = useHistory();
  const pincodeCheck = usePincodeCheck({
    pincode: data.address?.pincode,
    country: data.address?.country,
    onFound: (d) =>
      setData((prevData) => ({
        ...prevData,
        address: {
          ...prevData.address!,
          state:
            matchStateName(d.state, stateData.map((s) => s.name)) ?? d.state,
          city: d.city,
        },
      })),
  });
  const defaultWarehouseModel: WarehouseModel = warehouseDefaultValue;

  const handleOptionChange = (value: WarehouseModel["type"]) => {
    setData((pv) => ({ ...pv, type: value }));
  };

  // States for countries, states, and cities
  const [countries, setCountries] = useState<CountryType[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  console.log("selectedCountry:", selectedCountry);
  const [selectedEmployees, setSelectedEmployees] = useState<UserModel[]>([]);

  // Load countries on component mount
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
  }, []);

  const submit = async () => {
    try {
      const missing: string[] = [];
      if (!data.name?.trim()) missing.push("Warehouse Name");
      if (!data.sub_level) missing.push("Sub-Level");
      if (data.sub_level === "Manpower" && !data.vendor_code)
        missing.push("Vendor Code");
      if (!data.address?.line1?.trim()) missing.push("Address Line 1");
      if (!data.address?.country?.trim()) missing.push("Country");
      if (!data.address?.city?.trim()) missing.push("City");
      const isIndia = data.address?.country?.trim().toLowerCase() === "india";
      if (isIndia && !data.address?.state?.trim()) missing.push("State");
      if (isIndia && !data.address?.pincode?.toString().trim())
        missing.push("Pincode");

      if (missing.length) {
        showToast({
          type: ToastType.error,
          text: `Please fill the required field(s): ${missing.join(", ")}`,
        });
        return;
      }
      const pincodeError = pincodeCheck.validate(data.address?.state);
      if (pincodeError) {
        showToast({ type: ToastType.error, text: pincodeError });
        return;
      }

      // if (data.address?.pincode?.toString().trim().length !== 6) {
      //   showToast({
      //     type: ToastType.error,
      //     text: "Pincode must be exactly 6 characters",
      //   });
      //   return;
      // }

      setLoading(true);
      // Vendor code is optional for non-Manpower sub-levels but should still be
      // saved when provided, so it is no longer stripped for internal types.
      const updatedData = data;

      const res = data.id
        ? await fetchPut(`${url}/${data.id}`, updatedData)
        : await fetchPost(url, updatedData);

      // const res = data.id
      //   ? await fetchPut(`${url}/${data.id}`, data)
      //   : await fetchPost(url, data);
      if (res.success) {
        showToast({ type: ToastType.success, text: res.message });
        if (!data.id) {
          setData(defaultWarehouseModel);
          // setShowSucess(true);
        }
        setData((prevData) => ({
          ...prevData,
          ...res.data, // Update with the response data
        }));
        if (props.onClose) props.onClose(res.data);
        navaigation.push(urls.procurement_masters_warehouse);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      console.error("Error submitting data:", error);
      showToast({ type: ToastType.error, text: "An error occurred" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setEditable(edit);
    if (id && isUpdatePage) {
      getData(id);
    }
  }, []);

  useEffect(() => {
    // Initialize form with existing data if editing
    if (edit && props.data) {
      setData(props.data);
    }
  }, [edit, props.data]);

  const getData = async (id: string) => {
    try {
      setLoading(true);
      const res = await fetchGet(`${url}/${id}`);
      if (res.success) {
        console.log(res.data);
        const updatedData = { ...res.data, type: res.data.type }; // Update type property
        setData(updatedData); // Update state with merged data
      } else {
        console.log("Invalid");
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({ type: ToastType.error, text: "An error occurred" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mobile-shrink">
      <div
        id="warehouse_create_form"
        className="grid grid-cols-1 m-2 md:m-5 gap-2 select-none text-sm"
      >
        <h1 className="text-xl font-bold border-l-4 border-myPrimaryColor pl-2">
          Warehouse {id ? `Update: ${data.id}` : "Create"}
        </h1>
        <div className="grid grid-cols-1 w-auto md:w-full justify-center md:m-5 md:mr-5 gap-2 select-none">
          <div className="p-3 md:p-10 flex flex-col md:mr-10 gap-5 shadow bg-white rounded-lg">
            <div className="flex flex-col text-sm w-full md:w-1/2">
              <div className="flex flex-col items-start text-sm w-full my-2">
                <label className="font-semibold w-full" htmlFor="name">
                  Warehouse Name*
                </label>
                <input
                  placeholder="Enter Name"
                  className="p-1 rounded focus:outline-none w-full border"
                  id="name"
                  name="name"
                  value={data.name || ""}
                  required
                  type="text"
                  onChange={(e) => setData({ ...data, name: e.target.value })}
                />
              </div>
              {/* <div className="mb-2"> */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-5 items-end my-2">
                <div className="flex flex-col items-start text-sm w-full">
                  <label className="font-semibold w-full" htmlFor="line1">
                    Address Line 1 <RequiredMark />
                  </label>
                  <input
                    required
                    placeholder="address line 1"
                    className="p-1 rounded focus:outline-none w-full border"
                    id="line1"
                    name="line1"
                    value={data.address?.line1 || ""}
                    onChange={(e) =>
                      setData((prevData) => ({
                        ...prevData,
                        address: {
                          ...prevData.address!,
                          line1: e.target.value,
                        },
                      }))
                    }
                  />
                </div>

                <div className="flex flex-col items-start text-sm w-full">
                  <label className="font-semibold w-full" htmlFor="line2">
                    Address Line 2
                  </label>
                  <input
                    placeholder="address line 2"
                    className="p-1 rounded focus:outline-none w-full border"
                    id="line2"
                    name="line2"
                    value={data.address?.line2 || ""}
                    onChange={(e) =>
                      setData((prevData) => ({
                        ...prevData,
                        address: {
                          ...prevData.address!,
                          line2: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
              </div>
              <div className="flex flex-col items-start text-sm w-full my-2">
                <label className="font-semibold w-full" htmlFor="line2">
                  Address Line 3
                </label>
                <input
                  placeholder="address line3"
                  className="p-1 rounded focus:outline-none w-full border"
                  id="line2"
                  name="line2"
                  value={data.address?.line3 || ""}
                  onChange={(e) =>
                    setData((prevData) => ({
                      ...prevData,
                      address: {
                        ...prevData.address!,
                        line3: e.target.value,
                      },
                    }))
                  }
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-5 items-end my-2">
                <div className="flex flex-col items-start text-sm w-full">
                  <label className="font-semibold w-full" htmlFor="country">
                    Country*
                  </label>
                  <select
                    id="mail_detail.address.country"
                    name="mail_detail.address.country"
                    className="p-1 rounded focus:outline-none w-full border"
                    value={data?.address?.country || ""}
                    onChange={(event) => {
                      const selectedCountryName = event.target.value;

                      if (selectedCountryName) {
                        setSelectedCountry(selectedCountryName);
                      }
                      console.log("selectedCountry:", selectedCountryName);
                      setData((prev: any) => ({
                        ...prev,
                        address: {
                          ...prev.address,
                          country: selectedCountryName, // Store the country name in state
                        },
                      }));
                    }}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.isoCode} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedCountry === "India" ? (
                  <div className="grid gap-1">
                    <label
                      htmlFor="mail_detail.address.state"
                      className="font-semibold text-sm"
                    >
                      State <RequiredMark />
                    </label>
                    <select
                      id="mail_detail.address.state"
                      name="mail_detail.address.state"
                      className="p-1 rounded focus:outline-none w-full border"
                      value={data?.address?.state} // Make sure it defaults to empty string if undefined
                      onChange={(newValue) => {
                        const selectedStateValue = (
                          newValue.target as HTMLSelectElement
                        ).value;

                        setData((prev: any) => ({
                          ...prev,

                          address: {
                            ...prev.address,
                            state: selectedStateValue ? selectedStateValue : "", // Update the country field in the vendor state
                          },
                        }));
                      }}
                    >
                      <option value="">Select State</option>
                      {stateData.map((state) => (
                        <option key={state.id} value={state.name}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="grid gap-1">
                    <label
                      htmlFor="mail_detail.address.state"
                      className="font-semibold text-sm"
                    >
                      State
                    </label>
                    <input
                      type="text"
                      data-type={dataTypes.string}
                      className="border rounded-md px-3 py-1"
                      id="address.state"
                      name="address.state"
                      value={data?.address?.state}
                      onChange={(e) => {
                        setData((prev: any) => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            state: e.target.value ?? "", // Update the country field in the vendor state
                          },
                        }));
                      }}
                      placeholder="State"
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-5 items-end my-2">
                <div className="flex flex-col items-start text-sm w-full">
                  <label className="font-semibold w-full" htmlFor="city">
                    City*
                  </label>

                  <input
                    id="city"
                    name="city"
                    className="p-1 rounded focus:outline-none w-full border"
                    value={data?.address?.city || ""} // Make sure it defaults to empty string if undefined
                    onChange={(e) => {
                      setData((prev: any) => ({
                        ...prev,
                        address: {
                          ...prev.address,
                          city: e.target.value, // Update the country field in the vendor state
                        },
                      }));
                    }}
                  ></input>
                </div>
                <div className="flex flex-col items-start text-sm w-full">
                  <label className="font-semibold w-full" htmlFor="pincode">
                    Pincode*
                  </label>
                  <input
                    placeholder="Enter pincode"
                    className="p-1 rounded focus:outline-none w-full border"
                    id="pincode"
                    name="pincode"
                    required
                    value={data.address?.pincode || ""}
                    onChange={(e) => {
                      pincodeCheck.markTyped();
                      setData((prevData) => ({
                        ...prevData,
                        address: {
                          ...prevData.address!,
                          pincode: e.target.value,
                        },
                      }));
                    }}
                  />
                  <PincodeHint check={pincodeCheck} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-5 items-end my-2">
                {/* Sub-Level (Warehouse Category) — derives type in the backend */}
                <div className="flex flex-col items-start text-sm w-full">
                  <label className="font-semibold w-full" htmlFor="sub_level">
                    Sub-Level*
                  </label>
                  <select
                    id="sub_level"
                    value={data.sub_level || ""}
                    onChange={(e) => {
                      const sub_level = e.target.value;
                      setData({
                        ...data,
                        sub_level,
                        type: subLevelToType(sub_level),
                        phone_number: data.phone_number,
                        vendor_code: data.vendor_code,
                      });
                    }}
                    className="p-1 rounded focus:outline-none w-full border"
                  >
                    <option value="">select</option>
                    {WAREHOUSE_SUB_LEVEL_OPTIONS.map((s: string) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Internal SPOC */}
              {/* {data.type === "internal" &&
                data.sub_type === "city engineer warehouse" && (
                  <div className="flex flex-col items-start text-sm w-full md:w-1/2 my-2">
                    <label className="font-semibold w-full">
                      User Name/SPOC *
                    </label>
                    <EmployeeSearchWidget
                      disabled={!editable && data.id}
                      withBtn={false}
                      onSelect={(emp) => {
                        setData((pv) => ({
                          ...pv!,
                          spoc: {
                            ...pv.spoc,
                            uid: emp.uid,
                            contact_name:
                              emp.first_name + " " + (emp.last_name ?? ""),
                            contact_mobile: emp.mobile,
                            contact_email: emp.email,
                            department: emp.emp_profile?.department,
                          },
                        }));
                      }}
                      emp={
                        data.spoc?.uid
                          ? {
                              uid: data.spoc.uid!,
                              name: data.spoc.contact_name,
                            }
                          : undefined
                      }
                    />
                  </div>
                )} */}

              {/* Warehouse contact / vendor details */}
              {data.sub_level && (
                <div className="flex flex-col items-start text-sm w-full md:w-1/2 my-2">
                  <label className="font-semibold w-full">Phone Number</label>
                  <input
                    type="text"
                    id="phone_number"
                    value={data.phone_number}
                    onChange={(e) => {
                      setData((prev) => ({
                        ...prev,
                        phone_number: Number(e.target.value) || 0,
                      }));
                    }}
                    className="p-1 rounded focus:outline-none w-full border"
                  />
                </div>
              )}

              {data.sub_level && (
                <div className="flex flex-col items-start text-sm w-full md:w-1/2 my-2">
                  <label className="font-semibold w-full">
                    Vendor Code
                    {data.sub_level === "Manpower" && <RequiredMark />}
                  </label>
                  <PartySearchField
                    placeholder={data.vendor_code || ""}
                    onSelect={(e) => {
                      setData((prevData) => ({
                        ...prevData,
                        vendor_code: e.id,
                      }));
                    }}
                    status="Approved"
                  />
                </div>
              )}
            </div>
          </div>
          {/* </div> */}
        </div>
        {!id && (
          <div className="flex flex-wrap justify-end gap-5 mt-5 mb-2 md:mr-5">
            <button
              id="create_btn"
              onClick={submit}
              type="button"
              className="rounded-md text-sm font-semibold bg-myPrimaryColor text-white px-3 py-1"
            >
              Create
            </button>
            <button
              id="quit_btn"
              onClick={() => {
                navaigation.push(urls.procurement_masters_warehouse);
              }}
              type="button"
              className="rounded-md text-sm font-semibold bg-white border border-green-600 text-green-600 px-3 py-1"
            >
              Quit
            </button>
          </div>
        )}
        {id && (
          <div className="flex flex-wrap justify-end gap-5 mt-5 mb-2 md:mr-5">
            <button
              id="update_btn"
              onClick={submit}
              type="button"
              className="rounded-md text-sm font-semibold bg-myPrimaryColor text-white px-3 py-1"
            >
              Update
            </button>
            <button
              id="quit_btn"
              onClick={() => {
                navaigation.push(urls.procurement_masters_warehouse);
              }}
              type="button"
              className="rounded-md text-sm font-semibold bg-white border border-green-600 text-green-600 px-3 py-1"
            >
              Quit
            </button>
          </div>
        )}
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
};

export default InventoryBasketCreateScreen;
